import React, { useState } from 'react';
import { Network, AlertTriangle, Plus, Building2, Home } from 'lucide-react';
import { useEmailRelationshipContext } from '../../hooks/useEmailRelationshipContext';
import { useRoleLabels } from '../../hooks/useRoleLabels';
import { UNCURATED_METHODS } from '../../constants/relationships';
import EntityLink from '../shared/EntityLink';
import MatchMethodBadge from './MatchMethodBadge';
import RoleChips from './RoleChips';
import RelationshipCurateModal from './RelationshipCurateModal';

// Cabeçalho do card — em escopo de módulo para não remontar a cada render do pai.
function CardHeader({ matchMethod }) {
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-600 flex items-center gap-1">
        <Network size={11} /> Relacionamento do remetente
      </div>
      {matchMethod && <MatchMethodBadge method={matchMethod} className="ml-auto" />}
    </div>
  );
}

/**
 * G5E — EmailRelationshipCard.
 * Card de EXPLICABILIDADE do relacionamento do remetente de um e-mail.
 * Chama resolve_email_relationship_context(from, to, cc) e renderiza:
 *  empresa resolvida, badge de match_method, papéis aprovados (chips), imóveis
 *  (nome + matrícula + posse), domain hint, alerta de divergência domínio×relação
 *  aprovada e, nos estados sem relação curada, um CTA para curar (via RPC).
 *
 * Props: { fromEmail: string, toEmails?: string[], ccEmails?: string[] }
 */
export default function EmailRelationshipCard({ fromEmail, toEmails, ccEmails }) {
  const { context, loading, error, refetch } = useEmailRelationshipContext({ fromEmail, toEmails, ccEmails });
  const { roleLabel } = useRoleLabels();
  const [curateOpen, setCurateOpen] = useState(false);

  if (!fromEmail) return null;

  const wrap = 'p-4 border-b border-slate-100 bg-slate-50/40 space-y-2';

  if (loading) {
    return (
      <div className={wrap}>
        <CardHeader matchMethod={context?.match_method} />
        <div className="text-xs text-slate-500">Resolvendo relacionamento…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={wrap}>
        <CardHeader matchMethod={context?.match_method} />
        <div className="text-[11px] text-red-700 bg-red-50 border border-red-200 rounded p-2">
          Não foi possível resolver o relacionamento: {error}
        </div>
      </div>
    );
  }

  if (!context) return null;

  const empresas = Array.isArray(context.represented_empresas) ? context.represented_empresas : [];
  const imoveis = Array.isArray(context.represented_imoveis) ? context.represented_imoveis : [];
  const hint = context.domain_hint || null;
  const isPartnerDomain = hint?.domain_class === 'partner_domain';
  const domainDiverges =
    hint?.empresa_id && context.primary_empresa_id && hint.empresa_id !== context.primary_empresa_id;
  const uncurated = UNCURATED_METHODS.includes(context.match_method);
  const contactName = context.contact?.name;

  return (
    <div className={wrap}>
      <CardHeader matchMethod={context?.match_method} />

      {context.is_internal_sender && (
        <div className="text-[11px] text-slate-600 bg-slate-100 rounded px-2 py-1">Remetente interno (Aston).</div>
      )}

      {/* Empresa resolvida */}
      <div className="text-xs flex items-center gap-1.5 flex-wrap">
        <Building2 size={12} className="text-slate-500 flex-shrink-0" />
        <span className="text-slate-500">Empresa:</span>
        {context.primary_empresa_id ? (
          <EntityLink
            entityType="empresa"
            entityId={context.primary_empresa_id}
            label={context.primary_empresa_name}
            showIcon={false}
          />
        ) : (
          <span className="text-slate-400">— sem empresa resolvida</span>
        )}
        {contactName && <span className="text-slate-400">· {contactName}</span>}
      </div>

      {/* Papéis aprovados, por empresa representada */}
      {empresas.length > 0 && (
        <div className="text-xs space-y-1">
          {empresas.map((e) => (
            <div key={e.empresa_id} className="flex items-center gap-1.5 flex-wrap">
              <EntityLink entityType="empresa" entityId={e.empresa_id} label={e.name} showIcon={false} />
              {e.is_primary && (
                <span className="text-[9px] uppercase font-semibold text-emerald-700 bg-emerald-100 rounded px-1 py-0.5">
                  principal
                </span>
              )}
              <RoleChips roles={e.roles} roleLabel={roleLabel} />
            </div>
          ))}
        </div>
      )}

      {/* Imóveis relacionados (nome + matrícula + posse) */}
      {imoveis.length > 0 && (
        <div className="text-xs space-y-1">
          <div className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold flex items-center gap-1">
            <Home size={11} /> Imóveis
          </div>
          {imoveis.map((im) => (
            <div key={im.imovel_id} className="pl-1 space-y-0.5">
              <div className="flex items-center gap-1.5 flex-wrap">
                <EntityLink entityType="imovel" entityId={im.imovel_id} label={im.nome} showIcon={false} />
                {im.matricula && <span className="text-[10px] text-slate-500">mat. {im.matricula}</span>}
                <RoleChips roles={im.roles} roleLabel={roleLabel} />
              </div>
              {Array.isArray(im.ownership) && im.ownership.length > 0 && (
                <div className="text-[10px] text-slate-500 pl-1">
                  Posse:{' '}
                  {im.ownership.map((o, i) => (
                    <span key={`${o.owner_type}:${o.owner_id}:${i}`}>
                      {i > 0 ? '; ' : ''}
                      {o.owner_name}
                      {o.percentual != null ? ` (${o.percentual}%)` : ''}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Domain hint */}
      {hint?.dominio && (
        <div className="text-[11px] text-slate-600">
          <span>
            Domínio: <span className="font-medium">{hint.dominio}</span>
            {hint.empresa_name && (
              <>
                {' → '}
                {hint.empresa_id ? (
                  <EntityLink entityType="empresa" entityId={hint.empresa_id} label={hint.empresa_name} showIcon={false} />
                ) : (
                  hint.empresa_name
                )}
              </>
            )}
          </span>
          {isPartnerDomain && (
            <div className="text-[10px] text-slate-500 italic">
              Tratado como escritório/empregador, não cliente proprietário.
            </div>
          )}
        </div>
      )}

      {/* Alerta: domínio difere da relação aprovada */}
      {domainDiverges && (
        <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded p-2 flex items-start gap-1.5">
          <AlertTriangle size={13} className="flex-shrink-0 mt-0.5 text-amber-600" />
          <span>
            Domínio difere da relação aprovada — o domínio aponta para <strong>{hint.empresa_name}</strong>, mas a
            relação curada é <strong>{context.primary_empresa_name || '—'}</strong>.
          </span>
        </div>
      )}

      {/* Estado sem relação curada → CTA de curadoria */}
      {uncurated && (
        <div className="text-[11px] text-slate-600 bg-white border border-slate-200 rounded p-2 flex items-center justify-between gap-2">
          <span>Sem relação curada para este remetente.</span>
          <button
            onClick={() => setCurateOpen(true)}
            className="px-2 py-1 text-[11px] bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center gap-1 flex-shrink-0"
          >
            <Plus size={12} /> Curar
          </button>
        </div>
      )}

      {curateOpen && (
        <RelationshipCurateModal
          defaultContato={
            context.contact?.contato_id
              ? { id: context.contact.contato_id, label: context.contact.name || fromEmail }
              : null
          }
          defaultEntityType={imoveis.length > 0 && empresas.length === 0 ? 'imovel' : 'empresa'}
          onSaved={refetch}
          onClose={() => setCurateOpen(false)}
        />
      )}
    </div>
  );
}
